/**
 * TechnicalLogBookEntry-part3.js
 * 
 * Sector Form Rendering Component - Part 3
 * 
 * This file contains the second half of the renderSectorForm function which renders:
 * - Defect Report
 * - Inspections
 * - MEL Section
 * - BFF Certification
 * - Pilot Acceptance
 * - Engine Cleaning Details BFF
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SignatureField from './SignatureField';
import {
  getAmeSignaturePresetsForField
} from '../../utils/technicalLogBookAmeSignature';
import { tlbFormatFieldProps } from '../../utils/technicalLogBookFieldFormat';
import SectorCrsFormButton from './SectorCrsFormDialog';

/**
 * Render the second half of sector form (Defect Report onwards)
 * @param {Object} params - Parameters object
 * @param {String} params.sectorKey - Sector key (e.g., 'sector1')
 * @param {Object} params.sector - Sector data object
 * @param {Boolean} params.isReadOnly - Whether form is read-only
 * @param {Function} params.updateSectorField - Function to update sector field
 * @param {Function} params.updateSectorArrayField - Function to update sector array field
 * @param {Function} params.addInspection - Function to add inspection
 * @param {Function} params.removeInspection - Function to remove inspection
 * @param {String} params.entityId - Entity ID for signature/media records
 * @param {String} params.entityType - Entity type for signature/media records
 * @param {String[]} params.pilotNamePresets - PIC / co-pilot names for name-signature quick select
 * @param {String[]} params.previousAmeNames - AME names already used on this entry for freeSolo pickers
 * @returns {JSX.Element} - Sector form JSX
 */
export const renderSectorFormPart3 = ({
  sectorKey,
  sector,
  isReadOnly,
  updateSectorField,
  updateAmeSignatureField,
  updateInspectionAmeSignatureField,
  updateSectorArrayField,
  addInspection,
  removeInspection,
  entityId = null,
  entityType = 'TECHNICAL_LOG_BOOK',
  pilotNamePresets = [],
  previousAmeNames = []
}) => {
  const ameNamesExcluding = (currentValue) =>
    (previousAmeNames || []).filter((n) => n !== String(currentValue || '').trim());
  const sectorSlot = Number(String(sectorKey || '').replace('sector', '')) || null;

  return (
    <>
      {/* Defect Report */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Defect Report</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Pilot Defect Report"
              placeholder="NIL if none"
              value={sector.defectReport?.pilotDefectReport || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.pilotDefectReport', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pilot CHPL Number"
              value={sector.defectReport?.pilotCHPLNumber || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.pilotCHPLNumber', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Pilot Defect Time"
              placeholder="HH:mm"
              value={sector.defectReport?.pilotDefectTime || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.pilotDefectTime', e.target.value)}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.defectReport?.pilotDefectTime, 'defectReport.pilotDefectTime')}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="Pilot Signature (CHPL)"
              value={sector.defectReport?.pilotSignatureCHPL || ''}
              signatureType={sector.defectReport?.pilotSignatureCHPLType || 'NAME'}
              onChange={(value, type) => {
                updateSectorField(sectorKey, 'defectReport.pilotSignatureCHPL', value);
                updateSectorField(sectorKey, 'defectReport.pilotSignatureCHPLType', type);
              }}
              disabled={isReadOnly}
              helperText="Pilot signature with CHPL number for defect report"
              entityId={entityId}
              entityType={entityType}
              namePresetOptions={pilotNamePresets}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="AME Auth Number"
              value={sector.defectReport?.ameAuthNumber || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.ameAuthNumber', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="AME Defect Time"
              placeholder="HHmm"
              value={sector.defectReport?.ameDefectTime || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.ameDefectTime', e.target.value)}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.defectReport?.ameDefectTime, 'defectReport.ameDefectTime')}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="AME Defect Report"
              placeholder="NIL if none"
              value={sector.defectReport?.ameDefectReport || ''}
              onChange={(e) => updateSectorField(sectorKey, 'defectReport.ameDefectReport', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <SignatureField
              label="AME Signature (Authorized)"
              value={sector.defectReport?.ameSignatureAuth || ''}
              signatureType={sector.defectReport?.ameSignatureAuthType || 'NAME'}
              onChange={(value, type) => {
                updateAmeSignatureField(
                  sectorKey,
                  'defectReport.ameSignatureAuth',
                  'defectReport.ameSignatureAuthType',
                  value,
                  type
                );
              }}
              disabled={isReadOnly}
              helperText="AME authorized signature for defect report"
              entityId={entityId}
              entityType={entityType}
              ameNameOptions={ameNamesExcluding(sector.defectReport?.ameSignatureAuth)}
              ameSignaturePresets={getAmeSignaturePresetsForField(sector, 'defectReport.ameSignatureAuth')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Inspections */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1">Inspections</Typography>
          {!isReadOnly && (
            <Button size="small" onClick={() => addInspection(sectorKey)}>
              Add Inspection
            </Button>
          )}
        </Box>
        {(sector.inspections || []).map((inspection, idx) => (
          <Accordion key={idx} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Inspection {idx + 1}: {inspection.typeOfInspection || 'Not specified'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Type of Inspection"
                    value={inspection.typeOfInspection || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'typeOfInspection', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Task Card Issued"
                    value={inspection.taskCardIssued || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'taskCardIssued', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Task Card Reviewed"
                    value={inspection.taskCardReviewed || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'taskCardReviewed', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Auth/CHPL Number"
                    value={inspection.authNumberCHPLNumber || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'authNumberCHPLNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Inspection Time"
                    placeholder="HHmm"
                    value={inspection.inspectionTime || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'inspectionTime', e.target.value)}
                    disabled={isReadOnly}
                    {...tlbFormatFieldProps(inspection.inspectionTime, `inspections.${idx}.inspectionTime`)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Inspection Date"
                      value={inspection.inspectionDate ? dayjs(inspection.inspectionDate) : null}
                      onChange={(date) => updateSectorArrayField(sectorKey, 'inspections', idx, 'inspectionDate', date ? date.toDate() : null)}
                      disabled={isReadOnly}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <SignatureField
                    label="Authorized Person / Pilot Signature"
                    value={inspection.authorizedPersonPilotSignature || ''}
                    signatureType={inspection.authorizedPersonPilotSignatureType || 'NAME'}
                    onChange={(value, type) => {
                      updateSectorArrayField(sectorKey, 'inspections', idx, 'authorizedPersonPilotSignature', value);
                      updateSectorArrayField(sectorKey, 'inspections', idx, 'authorizedPersonPilotSignatureType', type);
                    }}
                    disabled={isReadOnly}
                    helperText="Signature of authorized person or pilot for inspection"
                    entityId={entityId}
                    entityType={entityType}
                    namePresetOptions={pilotNamePresets}
                  />
                </Grid>
                
                {/* Oil Upliftment Records */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Oil Upliftment Records</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Engine Oil Upliftment LH (ML)"
                    value={inspection.oilUpliftmentRecords?.engineOilUpliftmentLH || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.engineOilUpliftmentLH', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="NIL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Engine Oil Upliftment RH (ML)"
                    value={inspection.oilUpliftmentRecords?.engineOilUpliftmentRH || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.engineOilUpliftmentRH', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="NIL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MGB Oil Uplift (ML)"
                    value={inspection.oilUpliftmentRecords?.mgbOilUplift || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.mgbOilUplift', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="NIL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MGB Oil Level"
                    value={inspection.oilUpliftmentRecords?.mgbOilLevel || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.mgbOilLevel', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="SAT"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="TGB Oil Uplift (ML)"
                    value={inspection.oilUpliftmentRecords?.tgbOilUplift || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.tgbOilUplift', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="NIL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="TGB Oil Level"
                    value={inspection.oilUpliftmentRecords?.tgbOilLevel || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.tgbOilLevel', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="SAT"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hydraulic Fluid Uplift (ML)"
                    value={inspection.oilUpliftmentRecords?.hydraulicFluidUplift || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.hydraulicFluidUplift', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="NIL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hydraulic Fluid Level"
                    value={inspection.oilUpliftmentRecords?.hydraulicFluidLevel || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'oilUpliftmentRecords.hydraulicFluidLevel', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="SAT"
                  />
                </Grid>
                
                {/* Engine Cleaning Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Engine Cleaning Details</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>LH Engine External Wash</InputLabel>
                    <Select
                      value={inspection.engineCleaningDetails?.lhEngineExternalWash || ''}
                      label="LH Engine External Wash"
                      onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'engineCleaningDetails.lhEngineExternalWash', e.target.value)}
                      disabled={isReadOnly}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="NIL">NIL</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>RH Engine External Wash</InputLabel>
                    <Select
                      value={inspection.engineCleaningDetails?.rhEngineExternalWash || ''}
                      label="RH Engine External Wash"
                      onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'engineCleaningDetails.rhEngineExternalWash', e.target.value)}
                      disabled={isReadOnly}
                    >
                      <MenuItem value="">Select...</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="NIL">NIL</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Auth Number"
                    value={inspection.engineCleaningDetails?.authNumber || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'engineCleaningDetails.authNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Cleaning Time"
                    placeholder="HHmm"
                    value={inspection.engineCleaningDetails?.cleaningTime || ''}
                    onChange={(e) => updateSectorArrayField(sectorKey, 'inspections', idx, 'engineCleaningDetails.cleaningTime', e.target.value)}
                    disabled={isReadOnly}
                    {...tlbFormatFieldProps(inspection.engineCleaningDetails?.cleaningTime, `inspections.${idx}.cleaningTime`)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Cleaning Date"
                      value={inspection.engineCleaningDetails?.cleaningDate ? dayjs(inspection.engineCleaningDetails.cleaningDate) : null}
                      onChange={(date) => updateSectorArrayField(sectorKey, 'inspections', idx, 'engineCleaningDetails.cleaningDate', date ? date.toDate() : null)}
                      disabled={isReadOnly}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <SignatureField
                    label="AME / Auth Person Signature (Engine Cleaning)"
                    value={inspection.engineCleaningDetails?.ameAuthPersonSignature || ''}
                    signatureType={inspection.engineCleaningDetails?.ameAuthPersonSignatureType || 'NAME'}
                    onChange={(value, type) => {
                      updateInspectionAmeSignatureField(sectorKey, idx, value, type);
                    }}
                    disabled={isReadOnly}
                    helperText="AME or Authorized Person signature for engine cleaning"
                    entityId={entityId}
                    entityType={entityType}
                    ameNameOptions={ameNamesExcluding(inspection.engineCleaningDetails?.ameAuthPersonSignature)}
                    ameSignaturePresets={getAmeSignaturePresetsForField(
                      sector,
                      `inspections.${idx}.engineCleaningDetails.ameAuthPersonSignature`
                    )}
                  />
                </Grid>
                {!isReadOnly && (
                  <Grid item xs={12}>
                    <Button size="small" color="error" onClick={() => removeInspection(sectorKey, idx)}>
                      Remove Inspection
                    </Button>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
        {(!sector.inspections || sector.inspections.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No inspections added. Click "Add Inspection" to add one.
          </Typography>
        )}
      </Paper>

      {/* MEL Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>MEL Release</Typography>
        <Grid container spacing={6}>
          <Grid item xs={12} md={12}>
            <FormControl sx={{ 
             m: 1,
             width: { 
              xs: '100%', 
              md: '150px' 
              }}} >
              <InputLabel>A/C Released under MEL?</InputLabel>
              <Select
                value={sector.melReleased || ''}
                label="A/C Released under MEL?"
                onChange={(e) => updateSectorField(sectorKey, 'melReleased', e.target.value)}
                disabled={isReadOnly}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {sector.melReleased === 'Yes' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Section 4 Text"
                value={sector.melSection4Text || ''}
                disabled
                sx={{ 
                  backgroundColor: '#ffebee',
                  '& .MuiInputBase-input': { color: '#c62828' }
                }}
                helperText="Read-only - Shown in red when MEL is Yes"
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="AME Auth Number"
              value={sector.ameAuthNumber || ''}
              onChange={(e) => updateSectorField(sectorKey, 'ameAuthNumber', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="AME / Authorized Person Signature"
              value={sector.ameAuthorizedPersonSignature || ''}
              signatureType={sector.ameAuthorizedPersonSignatureType || 'NAME'}
              onChange={(value, type) => {
                updateAmeSignatureField(
                  sectorKey,
                  'ameAuthorizedPersonSignature',
                  'ameAuthorizedPersonSignatureType',
                  value,
                  type
                );
              }}
              disabled={isReadOnly}
              helperText="AME or Authorized Person signature for MEL release"
              entityId={entityId}
              entityType={entityType}
              ameNameOptions={ameNamesExcluding(sector.ameAuthorizedPersonSignature)}
              ameSignaturePresets={getAmeSignaturePresetsForField(sector, 'ameAuthorizedPersonSignature')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* BFF Certification */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1">BFF Certification</Typography>
          <SectorCrsFormButton
            entryId={entityId}
            sectorSlot={sectorSlot}
            sectorKey={sectorKey}
            isReadOnly={isReadOnly}
            updateSectorField={updateSectorField}
            savedCrsForm={sector.bffCertification?.crsForm}
          />
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="BFF Text"
              value={sector.bffCertification?.bffText || 'Certified that the BFF inspection has been carried out i.a.w approved'}
              disabled
              helperText="Read-only"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="BFF Task Card Issued"
              value={sector.bffCertification?.bffTaskCardIssued || ''}
              onChange={(e) => updateSectorField(sectorKey, 'bffCertification.bffTaskCardIssued', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="BFF Task Card Reviewed"
              value={sector.bffCertification?.bffTaskCardReviewed || ''}
              onChange={(e) => updateSectorField(sectorKey, 'bffCertification.bffTaskCardReviewed', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="BFF Equipment Text"
              value={sector.bffCertification?.bffEquipmentText || 'All operational and Emergency Equipment are correctly installed and found Serviceable'}
              disabled
              helperText="Read-only"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="AME Auth Number"
              value={sector.bffCertification?.ameAuthNumber || ''}
              onChange={(e) => updateSectorField(sectorKey, 'bffCertification.ameAuthNumber', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="BFF Date"
                value={sector.bffCertification?.bffDate ? dayjs(sector.bffCertification.bffDate) : null}
                onChange={(date) => updateSectorField(sectorKey, 'bffCertification.bffDate', date ? date.toDate() : null)}
                disabled={isReadOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="BFF Time"
              placeholder="HHmm"
              value={sector.bffCertification?.bffTime || ''}
              onChange={(e) => updateSectorField(sectorKey, 'bffCertification.bffTime', e.target.value)}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.bffCertification?.bffTime, 'bffCertification.bffTime')}
            />
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="AME / Authorized Person Signature (BFF)"
              value={sector.bffCertification?.ameAuthorizedPersonSignature || ''}
              signatureType={sector.bffCertification?.ameAuthorizedPersonSignatureType || 'NAME'}
              onChange={(value, type) => {
                updateAmeSignatureField(
                  sectorKey,
                  'bffCertification.ameAuthorizedPersonSignature',
                  'bffCertification.ameAuthorizedPersonSignatureType',
                  value,
                  type
                );
              }}
              disabled={isReadOnly}
              helperText="AME or Authorized Person signature for BFF certification"
              entityId={entityId}
              entityType={entityType}
              ameNameOptions={ameNamesExcluding(sector.bffCertification?.ameAuthorizedPersonSignature)}
              ameSignaturePresets={getAmeSignaturePresetsForField(
                sector,
                'bffCertification.ameAuthorizedPersonSignature'
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Pilot Acceptance */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Pilot Acceptance</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Acceptance Text"
              value={sector.pilotAcceptance?.acceptanceText || 'Certified that the conditions stipulated in CAR Section 8 Series \'O\' Part 4, Para 2.3.1 as well as Information in Sec 4 & 5 of this Tech log book has been read and understood.'}
              disabled
              helperText="Read-only"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="CHPL Number"
              value={sector.pilotAcceptance?.chplNumber || ''}
              onChange={(e) => updateSectorField(sectorKey, 'pilotAcceptance.chplNumber', e.target.value)}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Acceptance Time"
              placeholder="HHmm"
              value={sector.pilotAcceptance?.acceptanceTime || ''}
              onChange={(e) => updateSectorField(sectorKey, 'pilotAcceptance.acceptanceTime', e.target.value)}
              disabled={isReadOnly}
              {...tlbFormatFieldProps(sector.pilotAcceptance?.acceptanceTime, 'pilotAcceptance.acceptanceTime')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Acceptance Date"
                value={sector.pilotAcceptance?.acceptanceDate ? dayjs(sector.pilotAcceptance.acceptanceDate) : null}
                onChange={(date) => updateSectorField(sectorKey, 'pilotAcceptance.acceptanceDate', date ? date.toDate() : null)}
                disabled={isReadOnly}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <SignatureField
              label="Signature / CHPL Number"
              value={sector.pilotAcceptance?.signatureCHPLNumber || ''}
              signatureType={sector.pilotAcceptance?.signatureCHPLNumberType || 'NAME'}
              onChange={(value, type) => {
                updateSectorField(sectorKey, 'pilotAcceptance.signatureCHPLNumber', value);
                updateSectorField(sectorKey, 'pilotAcceptance.signatureCHPLNumberType', type);
              }}
              disabled={isReadOnly}
              helperText="Pilot signature with CHPL number for acceptance"
              entityId={entityId}
              entityType={entityType}
              namePresetOptions={pilotNamePresets}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Engine Cleaning Details BFF */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Engine Cleaning Details BFF</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
             <FormControl sx={{ 
                m: 1, 
               width: { 
              xs: '100%', 
              md: '150px' 
               } 
                }}>
              <InputLabel>External Wash</InputLabel>
              <Select
                value={sector.engineCleaningDetailsBFF?.externalWash || ''}
                label="External Wash"
                onChange={(e) => updateSectorField(sectorKey, 'engineCleaningDetailsBFF.externalWash', e.target.value)}
                disabled={isReadOnly}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
          <FormControl sx={{ 
                m: 1, 
               width: { 
              xs: '100%', 
              md: '175px' 
               } 
                }}>
              <InputLabel>Compressor Wash</InputLabel>
              <Select
                value={sector.engineCleaningDetailsBFF?.compressorWash || ''}
                label="Compressor Wash"
                onChange={(e) => updateSectorField(sectorKey, 'engineCleaningDetailsBFF.compressorWash', e.target.value)}
                disabled={isReadOnly}
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};
